import React, { useState, useMemo, useEffect, useContext } from "react";
import { Modal, message } from "antd";
import { ExclamationCircleOutlined } from "@ant-design/icons";

import { ScheduleTemplate } from "../../../types/scheduleTemplate";
import ScheduleTemplateFilters from "./components/scheduleTemplates/ScheduleTemplateFilters";
import ScheduleTemplateGrid from "./components/scheduleTemplates/ScheduleTemplateGrid";
import ScheduleTemplateModal from "./components/scheduleTemplates/ScheduleTemplateModal";
import { ScheduleTempOperation } from "../../../serviceoperation/ScheduleTempOperation";
import { CompanyContext } from "../../../contexts/company-context";

const { confirm } = Modal;

const ScheduleTemplatesTab: React.FC = () => {
  const [templates, setTemplates] = useState<ScheduleTemplate[]>([]);
  const { selectedCompany } = useContext(CompanyContext);
  const tempOps = new ScheduleTempOperation();

  useEffect(() => {
    setLoading(true);
    tempOps
      .getScheduleTempAsync(selectedCompany?.corporationId as number, 1, 255)
      .then((value) => {
        setTemplates(
          value.results.map((x) => ({
            id: x.templateId.toString(),
            name: x.name,
            description: x.name,
            startWorkHour: x.startHour,
            startWorkMin: x.startMinute,
            endWorkHour: x.endHour,
            endWorkMin: x.endMinute,
            isDefault: false,
            createdAt: x.createdDateTimeUTC,
            updatedAt: x.createdDateTimeUTC,
          }))
        );
        setLoading(false);
      });
  }, [selectedCompany]);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingTemplate, setEditingTemplate] =
    useState<ScheduleTemplate | null>(null);
  const [modalMode, setModalMode] = useState<"create" | "edit">("create");
  const [searchQuery, setSearchQuery] = useState("");
  const [loading, setLoading] = useState(false);

  const filteredTemplates = useMemo(
    () =>
      templates?.filter(
        (template) =>
          template.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
          template.description.toLowerCase().includes(searchQuery.toLowerCase())
      ),
    [templates, searchQuery]
  );

  const handleAddTemplate = () => {
    setModalMode("create");
    setEditingTemplate(null);
    setIsModalOpen(true);
  };

  const handleEditTemplate = (template: ScheduleTemplate) => {
    setModalMode("edit");
    setEditingTemplate(template);
    setIsModalOpen(true);
  };

  const handleSaveTemplate = (templateData: Partial<ScheduleTemplate>) => {
    if (modalMode === "create") {
      const newTemplate: ScheduleTemplate = {
        id: `template_${Date.now()}`,
        name: templateData.name!,
        description: templateData.description!,
        startWorkHour: templateData.startWorkHour!,
        startWorkMin: templateData.startWorkMin!,
        endWorkHour: templateData.endWorkHour!,
        endWorkMin: templateData.endWorkMin!,
        isDefault: templateData.isDefault || false,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      };

      // If setting as default, remove default from other templates
      if (newTemplate.isDefault) {
        setTemplates((prev) => prev.map((t) => ({ ...t, isDefault: false })));
      }

      setTemplates((prev) => [...prev, newTemplate]);
    } else if (editingTemplate) {
      setTemplates((prev) =>
        prev.map((template) =>
          template.id === editingTemplate.id
            ? {
                ...template,
                ...templateData,
                updatedAt: new Date().toISOString(),
              }
            : templateData.isDefault
            ? { ...template, isDefault: false }
            : template
        )
      );
    }
  };

  const handleDeleteTemplate = (id: string) => {
    const template = templates.find((t) => t.id === id);

    if (template?.isDefault) {
      message.error("Cannot delete the default template");
      return;
    }

    confirm({
      title: "Delete Schedule Template",
      icon: <ExclamationCircleOutlined />,
      content:
        "Are you sure you want to delete this schedule template? This action cannot be undone.",
      okText: "Delete",
      okType: "danger",
      cancelText: "Cancel",
      onOk() {
        tempOps
          .deleteScheduleTempAsync(
            selectedCompany?.corporationId as number,
            Number(id)
          )
          .then((value) => {
            if (value.statusCode === 200) {
              setTemplates((prev) =>
                prev.filter((template) => template.id !== id)
              );
              message.success("Schedule template deleted successfully");
            } else {
              message.error(
                "An error has occured while processing your request"
              );
            }
            setLoading(false);
          });
      },
    });
  };

  const handleSetDefault = (id: string) => {
    setTemplates((prev) =>
      prev.map((template) => ({
        ...template,
        isDefault: template.id === id,
        updatedAt:
          template.id === id ? new Date().toISOString() : template.updatedAt,
      }))
    );
    message.success("Default template updated successfully");
  };

  return (
    <div style={{ marginTop: 24 }}>
      <ScheduleTemplateFilters
        onSearch={setSearchQuery}
        onAddTemplate={handleAddTemplate}
        totalTemplates={filteredTemplates.length}
      />

      <div style={{ marginTop: 24 }}>
        <ScheduleTemplateGrid
          templates={filteredTemplates}
          loading={loading}
          onEdit={handleEditTemplate}
          onDelete={handleDeleteTemplate}
          onSetDefault={handleSetDefault}
        />
      </div>

      <ScheduleTemplateModal
        open={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
          setEditingTemplate(null);
        }}
        onSave={handleSaveTemplate}
        template={editingTemplate}
        mode={modalMode}
      />
    </div>
  );
};

export default ScheduleTemplatesTab;
